/**
 * Privacy mode (screen-share safe).
 *
 * A single global on/off preference that blurs sensitive deal and contact data
 * across the app so the operator can navigate and share their screen without
 * exposing private information. This is purely visual masking on the client; it
 * does not change what the server returns.
 *
 * - Stored in localStorage under key 'poe-privacy-mode' ('on' | 'off').
 * - Applied by toggling the `privacy-active` class on document.documentElement.
 * - FOUC prevention happens in index.html inline script before React mounts, so
 *   sensitive data is never shown unmasked on first paint.
 * - Multiple toggle instances (nav rail + mobile nav) and other browser tabs
 *   stay in sync via a custom event + the storage event.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'poe-privacy-mode';
const CHANGE_EVENT = 'poe-privacy-change';

/** The class applied to elements whose contents should be masked. */
export const MASK_CLASS = 'mask-sensitive';

/**
 * In-memory source of truth. localStorage may be unavailable or throw (private
 * browsing, blocked storage), in which case persistence is impossible but the
 * toggle must still work for the current session. We mirror the value here so a
 * blocked-storage read never silently reverts an enabled session back to off.
 */
let memoryPrivacy = false;

/** Read the current privacy preference right now (sync; SSR-safe). */
export function readStoredPrivacy(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'on') {
      memoryPrivacy = true;
    } else if (raw === 'off') {
      memoryPrivacy = false;
    }
    // raw === null (key absent / storage blocked-but-not-throwing): keep the
    // in-memory value so a session enabled this run is not lost.
    return memoryPrivacy;
  } catch {
    return memoryPrivacy;
  }
}

function applyPrivacy(enabled: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('privacy-active', enabled);
}

/**
 * usePrivacy - global privacy-mode hook.
 *
 * @returns { enabled, setEnabled, toggle }
 *   enabled:    whether sensitive data is currently masked
 *   setEnabled: set the preference (writes localStorage + applies + broadcasts)
 *   toggle:     flip the current preference
 */
export function usePrivacy() {
  const [enabled, setEnabledState] = useState<boolean>(readStoredPrivacy);

  // Sync this instance on mount and whenever another instance/tab changes it.
  useEffect(() => {
    const sync = (event?: Event) => {
      // Same-tab broadcasts carry the desired value in `detail`, so we never
      // re-read storage that may be blocked. Cross-tab `storage` events have no
      // detail; for those we read storage (the other tab persisted it there).
      const detail = (event as CustomEvent | undefined)?.detail;
      let next: boolean;
      if (typeof detail === 'boolean') {
        memoryPrivacy = detail;
        next = detail;
      } else {
        next = readStoredPrivacy();
      }
      setEnabledState(next);
      applyPrivacy(next);
    };
    sync();
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    memoryPrivacy = next;
    try {
      localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
    } catch {
      /* storage unavailable: in-memory value above keeps the session correct */
    }
    setEnabledState(next);
    applyPrivacy(next);
    try {
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
    } catch {
      try {
        window.dispatchEvent(new Event(CHANGE_EVENT));
      } catch {
        /* ignore */
      }
    }
  }, []);

  const toggle = useCallback(() => {
    setEnabled(!readStoredPrivacy());
  }, [setEnabled]);

  return { enabled, setEnabled, toggle };
}
